
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageTitle } from "@/components/PageTitle";
import { SectionTitle } from "@/components/SectionTitle";
import { Button } from "@/components/ui/button";
import { 
  Instagram, Camera, 
  ArrowRight, CalendarCheck, MessageSquare,
  Building, Users
} from "lucide-react";
import { TestimonialCard } from "@/components/TestimonialCard";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const Gallery = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Clinic images
  const clinicImages = [
    {
      src: "/lovable-uploads/03c19580-d6f0-49f6-9a9e-6ca80f87d642.png",
      alt: "Modern Dental Chair",
      category: "Clinic"
    },
    {
      src: "/lovable-uploads/64c44fc7-9a04-4e8e-8597-5e1776537537.png",
      alt: "Reception Area",
      category: "Clinic"
    },
    {
      src: "/lovable-uploads/7b821ae3-8420-4b12-b60d-6b0acd29f9fb.png",
      alt: "Dental OPG Machine",
      category: "Clinic"
    },
    {
      src: "/lovable-uploads/f578e872-9601-4c3f-bca2-0260505ab8a5.png",
      alt: "Patient Lounge Area",
      category: "Clinic"
    },
    {
      src: "/lovable-uploads/ebdbfff0-7237-4687-a102-2cecfdce9ba9.png",
      alt: "Treatment Room",
      category: "Clinic"
    },
    {
      src: "/lovable-uploads/f0524047-c9dd-4b15-944b-09c88dfe7559.png",
      alt: "Pediatric Dental Area",
      category: "Clinic"
    },
    {
      src: "/lovable-uploads/855f973b-22a9-4935-8eac-6853245003fd.png",
      alt: "Waiting Lounge",
      category: "Clinic"
    },
  ];
  
  // Team images
  const teamImages = [
    {
      src: "/lovable-uploads/5e0d5f26-be3e-4588-8bb9-949c9664ba52.png",
      alt: "Dr. Hemant Asrani and Dr. Karan Asrani",
      category: "Team"
    },
    {
      src: "/lovable-uploads/a0700abd-546f-4413-bac0-66e86a38c22a.png",
      alt: "Asrani Dental Team",
      category: "Team"
    },
    {
      src: "/lovable-uploads/ca1724a6-f68a-47f9-950b-2d60ef356bff.png",
      alt: "Dr. Hemant Asrani, Dr. Nidhi Asrani and Dr. Karan Asrani",
      category: "Team"
    }
  ];
  
  return (
    <div className="flex flex-col min-h-screen">
      <PageTitle title="Gallery – Asrani Dental Clinic" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-dental-offwhite">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <span className="inline-block text-dental-highlight font-medium mb-4 uppercase tracking-wider text-sm">
              Our Gallery
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6">
              Explore Our Dental Clinic
            </h1>
            <p className="text-lg text-dental-muted">
              Take a visual tour of our state-of-the-art facilities, meet our team, and see the beautiful smiles we've helped create.
            </p>
          </div>
        </div>
      </section>
      
      {/* Clinic Images Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <SectionTitle
            subtitle="Clinic Tour"
            title="Our State-of-the-Art Facilities"
            description="Explore our modern clinic designed for comfort and equipped with the latest dental technology."
            icon={<Building className="w-6 h-6 text-dental-highlight" />}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {clinicImages.map((image, index) => (
              <div 
                key={index} 
                className="relative overflow-hidden rounded-xl group hover-lift transition-all duration-300"
              >
                <img 
                  src={image.src} 
                  alt={image.alt} 
                  className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                  <span className="inline-block bg-dental-highlight/80 text-white text-xs px-3 py-1 rounded-full mb-2">
                    {image.category}
                  </span>
                  <h3 className="text-white font-medium">{image.alt}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Team Images Section */}
      <section className="py-16 md:py-24 bg-dental-offwhite">
        <div className="container-custom">
          <SectionTitle
            subtitle="Our Team"
            title="Meet Our Expert Dental Team"
            description="Get to know the skilled professionals dedicated to providing you with exceptional dental care."
            icon={<Users className="w-6 h-6 text-dental-highlight" />}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {teamImages.map((image, index) => (
              <div 
                key={index} 
                className="relative overflow-hidden rounded-xl group hover-lift transition-all duration-300"
              >
                <img 
                  src={image.src} 
                  alt={image.alt} 
                  className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                  <span className="inline-block bg-dental-highlight/80 text-white text-xs px-3 py-1 rounded-full mb-2">
                    {image.category}
                  </span>
                  <h3 className="text-white font-medium">{image.alt}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Patient Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <SectionTitle
            subtitle="Patient Stories"
            title="What Our Patients Say"
            description="Hear from our satisfied patients about their experiences at Asrani Dental Clinic."
          />
          
          <div className="mt-12">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full"
            >
              <CarouselContent>
                <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                  <TestimonialCard 
                    name="Rameshwari Sahu" 
                    comment="I recently visited Asrani Dental Sadar and was really impressed with their services. I had an OPG scan done, which was quick and painless, and the staff made sure I understood everything. They also handled my child's dental care wonderfully, making it a fun and stress-free experience for them. It's a great place for both adults and kids, with professional and caring staff. Highly recommend!" 
                    rating={5} 
                    image="/placeholder.svg" 
                  />
                </CarouselItem>
                <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                  <TestimonialCard 
                    name="Yash Ghoderao" 
                    comment="Had a great experience for my braces treatment, just started today. All the process of consultation to OPG and everything was at one place. Looking forward to my new smile in a year." 
                    rating={5} 
                    image="/placeholder.svg" 
                  />
                </CarouselItem>
                <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                  <TestimonialCard 
                    name="Shruti Nagarkar" 
                    comment="The most satisfying clinic experience for my whole family. Visited for my son's RCT treatment, and Dr. Karan was extremely patient and gentle with him. My son who was initially frightened is now comfortable visiting the dentist. Best dental care in the city!" 
                    rating={5} 
                    image="/placeholder.svg" 
                  />
                </CarouselItem>
              </CarouselContent>
              <CarouselPrevious className="left-0 sm:-left-12" />
              <CarouselNext className="right-0 sm:-right-12" />
            </Carousel>
          </div>
        </div>
      </section>
      
      {/* Instagram Feed Section */}
      <section className="py-16 md:py-24 bg-dental-offwhite">
        <div className="container-custom">
          <SectionTitle
            subtitle="Social Media"
            title="Follow Us on Instagram"
            description="Stay connected with our latest updates, educational content, and smile transformations by following us on social media."
          />
          
          <div className="flex flex-wrap justify-center gap-4 mt-12">
            {/* Instagram posts would go here */}
            <div className="w-full text-center">
              <Button 
                className="btn-primary flex items-center text-base group mx-auto"
                onClick={() => window.open("https://www.instagram.com", "_blank")}
              >
                <Instagram size={18} className="mr-2" />
                Follow Us on Instagram
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Would You Like to Be Featured Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="bg-dental-offwhite rounded-xl shadow-soft p-8 md:p-12 text-center max-w-4xl mx-auto">
            <Camera size={48} className="mx-auto text-dental-highlight mb-6" />
            <h2 className="text-3xl font-serif font-semibold mb-4">Would You Like to Be Featured?</h2>
            <p className="text-dental-muted mb-8 max-w-2xl mx-auto">
              At Asrani Dental Clinic, we're proud of the beautiful smiles we've created since 1986. If you've recently had treatment with us and would like to share your experience or your smile transformation, we'd be honored to feature you in our gallery. Simply send us your photos along with your consent, and you might see your smile showcased here!
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button 
                className="btn-primary flex items-center text-base group"
                onClick={() => window.location.href = '/contact'}
              >
                <MessageSquare size={18} className="mr-2" />
                Contact Us to Submit
              </Button>
              <Button 
                className="btn-secondary flex items-center text-base group"
                onClick={() => window.open("https://www.healthcoco.com/nagpur/doctor/dr-hemant-asrani-endodontist-dentist/USRH249D644", "_blank")}
              >
                <CalendarCheck size={18} className="mr-2" />
                Book Your Appointment
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Gallery;
